// ./src/contexts/SavedPlacesContext.tsx
//
// Context wrapper around the useSavedPlaces hook.
//
// Purpose:
// - Allows sibling components (list, edit drawer, place picker, travel drawers)
//   to share the same saved places state without prop-drilling.
// - Each page creates its own <SavedPlacesProvider> with its own config,
//   so SavedPlacesPage management doesn't affect travel drawer state, etc.
// - The underlying useSavedPlaces hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In a page component:
//   <SavedPlacesProvider autoLoad pageSize={100}>
//     <SavedPlacesPageContent />
//     <SavedPlaceEditDrawer />
//   </SavedPlacesProvider>
//
//   // In any child component:
//   const { savedPlaces, loading, create, ... } = useSavedPlacesContext();
//
//   // In a component that may or may not be inside a provider (e.g. place picker):
//   const context = useOptionalSavedPlacesContext();

import React, { createContext, useContext } from "react";

import { useSavedPlaces, UseSavedPlacesOptions, UseSavedPlacesReturn } from "@/hooks/useSavedPlaces";

// ============================================
// Context
// ============================================

const SavedPlacesContext = createContext<UseSavedPlacesReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface SavedPlacesProviderProps extends UseSavedPlacesOptions {
  children: React.ReactNode;
}

/**
 * Wraps useSavedPlaces into a context provider.
 *
 * Each instance creates its own independent useSavedPlaces store
 * with its own filters, pagination, loading state, and error state.
 *
 * @example
 * <SavedPlacesProvider autoLoad pageSize={100}>
 *   <MyPageContent />
 * </SavedPlacesProvider>
 */
export const SavedPlacesProvider: React.FC<SavedPlacesProviderProps> = ({
  children,
  ...options
}) => {
  const savedPlaces = useSavedPlaces(options);

  return (
    <SavedPlacesContext.Provider value={savedPlaces}>
      {children}
    </SavedPlacesContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the SavedPlacesContext. Throws if used outside a SavedPlacesProvider.
 *
 * Use this in components that are always rendered inside a provider
 * (e.g. SavedPlacesList, SavedPlacesToolbar).
 */
export const useSavedPlacesContext = (): UseSavedPlacesReturn => {
  const context = useContext(SavedPlacesContext);

  if (!context) {
    throw new Error(
      "useSavedPlacesContext must be used within a <SavedPlacesProvider>. " +
      "Wrap your component tree with <SavedPlacesProvider> or use useOptionalSavedPlacesContext() instead."
    );
  }

  return context;
};

/**
 * Access the SavedPlacesContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider
 * (e.g. SavedPlacePicker used in travel drawers that may have their
 * own provider, or in expense forms that don't).
 *
 * @example
 * const context = useOptionalSavedPlacesContext();
 * const places = context?.savedPlaces ?? [];
 */
export const useOptionalSavedPlacesContext = (): UseSavedPlacesReturn | null => {
  return useContext(SavedPlacesContext);
};